import * as React from 'react'
import { createPortal } from 'react-dom'
import { DatePickerPopup } from './DatePickerSingle'
import { faCalendar, FaIcon, faXmark } from '@/lib/fa'

type DatePickerSingleButtonProps = {
  placeholder?: string
  iconAlign?: 'left' | 'right'
  fullWidth?: boolean
  size?: 'small' | 'large'
  highlightSelected?: boolean
  action?: () => void
  clearable?: boolean
  confirmSelection?: boolean

  // controlled
  value: string | null
  setValue: (next: string | null) => void
}

export const DatePickerSingleButton: React.FC<DatePickerSingleButtonProps> = ({
  placeholder = 'RRRR-MM-DD',
  iconAlign = 'left',
  fullWidth = false,
  size = 'large',
  highlightSelected = false,
  action,
  clearable,
  confirmSelection = true,

  value,
  setValue,
}) => {
  const [open, setOpen] = React.useState(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })

  const handleConfirm = (fullDate: string) => {
    setValue(fullDate)
    setOpen(false)
    action?.()
  }

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const willOpen = !open
    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX,
      })
    }
    setOpen(willOpen)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setValue(null)
    setOpen(false)
  }

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="tw-relative tw-z-50 tw-inline-block">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        type="button"
        className={`tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-border tw-px-2 tw-py-1 tw-text-base-content hover:tw-bg-[#F5F5F5] ${
          open
            ? 'tw-border-2 tw-border-primary'
            : value && highlightSelected
              ? 'tw-border-primary tw-bg-[#F1F4FF]'
              : 'tw-border-neutral/20 tw-bg-transparent'
        } ${iconAlign === 'right' ? 'tw-flex-row-reverse' : 'tw-flex-row'} ${
          size === 'small' ? 'tw-py-1' : 'tw-py-2'
        } ${fullWidth ? 'tw-w-full' : ''}`}
      >
        {/* calendar icon instead of img */}
        <FaIcon
          icon={faCalendar}
          className="tw-h-4 tw-w-4 tw-transition-transform tw-duration-200"
        />

        <div className="tw-flex tw-items-center tw-gap-2">
          <p
            className={`tw-text-sm ${value && highlightSelected ? 'tw-text-primary' : ''}`}
          >
            {value || placeholder}
          </p>

          {clearable && value && (
            <span onClick={handleDeleteClick} className="tw-inline-flex">
              <FaIcon
                icon={faXmark}
                className="tw-h-4 tw-w-4 tw-transition-transform tw-duration-200"
              />
            </span>
          )}
        </div>
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tw-absolute tw-z-[9999] tw-shadow-md"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              transform: 'translateX(-100%)',
            }}
          >
            <DatePickerPopup
              value={value ?? null}
              submit={handleConfirm}
              confirmSelection={false} //{confirmSelection}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}
